/**
 * Property Type Configuration
 *
 * Central configuration for property type attributes display.
 * Defines which attributes are relevant for each property type category.
 */

// All property types from backend schema
export type PropertyTypeKey =
  // Residential
  | 'APARTMENT' | 'VILLA' | 'FLOOR' | 'RESIDENTIAL_BUILDING'
  | 'RESIDENTIAL_LAND' | 'REST_HOUSE' | 'CHALET' | 'ROOM'
  | 'TOWNHOUSE' | 'STUDIO' | 'PENTHOUSE' | 'DUPLEX'
  // Commercial
  | 'OFFICE' | 'COMMERCIAL_BUILDING' | 'WAREHOUSE' | 'COMMERCIAL_LAND'
  | 'INDUSTRIAL_LAND' | 'FARM' | 'AGRICULTURAL_LAND' | 'MALL'
  | 'HOTEL' | 'WORKSHOP' | 'FACTORY' | 'SCHOOL' | 'HEALTH_CENTER'
  | 'STATION' | 'SHOWROOM'
  // Legacy (frontend uses these)
  | 'HOUSE' | 'CONDO' | 'LAND' | 'COMMERCIAL' | 'BUILDING'
  // Lowercase variants (for compatibility)
  | 'House' | 'Apartment' | 'Condo' | 'Townhouse' | 'Studio' | 'Penthouse';

export type PropertyCategory =
  | 'residential'
  | 'land'
  | 'commercial'
  | 'hospitality'
  | 'institutional';

export type AttributeKey =
  | 'bedrooms'
  | 'bathrooms'
  | 'area'
  | 'parking'
  | 'yearBuilt'
  | 'floors'
  | 'furnished'
  | 'petFriendly'
  | 'orientation'
  | 'floorLevel'
  | 'unitNumber'
  | 'livingRooms';

export interface AttributeConfig {
  show: boolean;
  labelKey: string;
  labelKeyAr: string;
  icon: string;
}

export interface PropertyTypeConfig {
  category: PropertyCategory;
  icon: string;
  primaryAttributes: AttributeKey[];
  attributes: Partial<Record<AttributeKey, AttributeConfig>>;
  hideZeroValues: boolean;
}

// Property type to category mapping
const PROPERTY_TYPE_CATEGORIES: Record<string, PropertyCategory> = {
  // Residential with rooms
  APARTMENT: 'residential',
  Apartment: 'residential',
  VILLA: 'residential',
  HOUSE: 'residential',
  House: 'residential',
  TOWNHOUSE: 'residential',
  Townhouse: 'residential',
  STUDIO: 'residential',
  Studio: 'residential',
  PENTHOUSE: 'residential',
  Penthouse: 'residential',
  DUPLEX: 'residential',
  FLOOR: 'residential',
  ROOM: 'residential',
  CONDO: 'residential',
  Condo: 'residential',
  RESIDENTIAL_BUILDING: 'residential',

  // Land types (NO rooms/bathrooms)
  RESIDENTIAL_LAND: 'land',
  COMMERCIAL_LAND: 'land',
  INDUSTRIAL_LAND: 'land',
  AGRICULTURAL_LAND: 'land',
  LAND: 'land',
  FARM: 'land',

  // Commercial buildings
  OFFICE: 'commercial',
  WAREHOUSE: 'commercial',
  WORKSHOP: 'commercial',
  FACTORY: 'commercial',
  SHOWROOM: 'commercial',
  SHOP: 'commercial',
  COMMERCIAL_BUILDING: 'commercial',
  COMMERCIAL: 'commercial',
  BUILDING: 'commercial',

  // Hospitality
  HOTEL: 'hospitality',
  REST_HOUSE: 'hospitality',
  CHALET: 'hospitality',

  // Institutional
  SCHOOL: 'institutional',
  HEALTH_CENTER: 'institutional',
  MALL: 'institutional',
  STATION: 'institutional',
};

// Default attribute configs
const DEFAULT_ATTRIBUTES: Record<AttributeKey, AttributeConfig> = {
  bedrooms: {
    show: true,
    labelKey: 'Bedrooms',
    labelKeyAr: 'غرف النوم',
    icon: 'ri-hotel-bed-line',
  },
  bathrooms: {
    show: true,
    labelKey: 'Bathrooms',
    labelKeyAr: 'دورات المياه',
    icon: 'ri-drop-line',
  },
  area: {
    show: true,
    labelKey: 'Area',
    labelKeyAr: 'المساحة',
    icon: 'ri-fullscreen-line',
  },
  parking: {
    show: true,
    labelKey: 'Parking',
    labelKeyAr: 'مواقف',
    icon: 'ri-parking-line',
  },
  yearBuilt: {
    show: true,
    labelKey: 'Year Built',
    labelKeyAr: 'سنة البناء',
    icon: 'ri-calendar-line',
  },
  floors: {
    show: false,
    labelKey: 'Floors',
    labelKeyAr: 'الطوابق',
    icon: 'ri-stack-line',
  },
  furnished: {
    show: true,
    labelKey: 'Furnished',
    labelKeyAr: 'مفروشة',
    icon: 'ri-sofa-line',
  },
  petFriendly: {
    show: true,
    labelKey: 'Pet Friendly',
    labelKeyAr: 'يسمح بالحيوانات',
    icon: 'ri-bear-smile-line',
  },
  orientation: {
    show: false,
    labelKey: 'Orientation',
    labelKeyAr: 'الواجهة',
    icon: 'ri-compass-3-line',
  },
  floorLevel: {
    show: false,
    labelKey: 'Floor',
    labelKeyAr: 'الدور',
    icon: 'ri-stairs-line',
  },
  unitNumber: {
    show: false,
    labelKey: 'Unit',
    labelKeyAr: 'رقم الوحدة',
    icon: 'ri-door-open-line',
  },
  livingRooms: {
    show: false,
    labelKey: 'Living Rooms',
    labelKeyAr: 'غرف المعيشة',
    icon: 'ri-tv-2-line',
  },
};

// Category-level configurations
const CATEGORY_CONFIGS: Record<PropertyCategory, Partial<PropertyTypeConfig>> = {
  residential: {
    primaryAttributes: ['bedrooms', 'bathrooms', 'area', 'parking'],
    hideZeroValues: true,
    attributes: {
      bedrooms: { ...DEFAULT_ATTRIBUTES.bedrooms, show: true },
      bathrooms: { ...DEFAULT_ATTRIBUTES.bathrooms, show: true },
      area: { ...DEFAULT_ATTRIBUTES.area, show: true },
      parking: { ...DEFAULT_ATTRIBUTES.parking, show: true },
      yearBuilt: { ...DEFAULT_ATTRIBUTES.yearBuilt, show: true },
      furnished: { ...DEFAULT_ATTRIBUTES.furnished, show: true },
      petFriendly: { ...DEFAULT_ATTRIBUTES.petFriendly, show: true },
    },
  },
  land: {
    primaryAttributes: ['area', 'orientation'],
    hideZeroValues: true,
    attributes: {
      bedrooms: { ...DEFAULT_ATTRIBUTES.bedrooms, show: false },
      bathrooms: { ...DEFAULT_ATTRIBUTES.bathrooms, show: false },
      area: {
        show: true,
        labelKey: 'Plot Size',
        labelKeyAr: 'مساحة الأرض',
        icon: 'ri-fullscreen-line',
      },
      parking: { ...DEFAULT_ATTRIBUTES.parking, show: false },
      yearBuilt: { ...DEFAULT_ATTRIBUTES.yearBuilt, show: false },
      orientation: { ...DEFAULT_ATTRIBUTES.orientation, show: true },
      furnished: { ...DEFAULT_ATTRIBUTES.furnished, show: false },
      petFriendly: { ...DEFAULT_ATTRIBUTES.petFriendly, show: false },
    },
  },
  commercial: {
    primaryAttributes: ['area', 'floors', 'parking'],
    hideZeroValues: true,
    attributes: {
      bedrooms: {
        show: true,
        labelKey: 'Rooms',
        labelKeyAr: 'غرف',
        icon: 'ri-door-line',
      },
      bathrooms: { ...DEFAULT_ATTRIBUTES.bathrooms, show: true },
      area: { ...DEFAULT_ATTRIBUTES.area, show: true },
      parking: { ...DEFAULT_ATTRIBUTES.parking, show: true },
      yearBuilt: { ...DEFAULT_ATTRIBUTES.yearBuilt, show: true },
      floors: { ...DEFAULT_ATTRIBUTES.floors, show: true },
      furnished: { ...DEFAULT_ATTRIBUTES.furnished, show: false },
      petFriendly: { ...DEFAULT_ATTRIBUTES.petFriendly, show: false },
    },
  },
  hospitality: {
    primaryAttributes: ['bedrooms', 'bathrooms', 'area', 'floors'],
    hideZeroValues: true,
    attributes: {
      bedrooms: {
        show: true,
        labelKey: 'Rooms',
        labelKeyAr: 'غرف',
        icon: 'ri-hotel-bed-line',
      },
      bathrooms: { ...DEFAULT_ATTRIBUTES.bathrooms, show: true },
      area: { ...DEFAULT_ATTRIBUTES.area, show: true },
      parking: { ...DEFAULT_ATTRIBUTES.parking, show: true },
      yearBuilt: { ...DEFAULT_ATTRIBUTES.yearBuilt, show: true },
      floors: { ...DEFAULT_ATTRIBUTES.floors, show: true },
      furnished: { ...DEFAULT_ATTRIBUTES.furnished, show: true },
      petFriendly: { ...DEFAULT_ATTRIBUTES.petFriendly, show: false },
    },
  },
  institutional: {
    primaryAttributes: ['area', 'floors', 'parking'],
    hideZeroValues: true,
    attributes: {
      bedrooms: { ...DEFAULT_ATTRIBUTES.bedrooms, show: false },
      bathrooms: { ...DEFAULT_ATTRIBUTES.bathrooms, show: true },
      area: { ...DEFAULT_ATTRIBUTES.area, show: true },
      parking: { ...DEFAULT_ATTRIBUTES.parking, show: true },
      yearBuilt: { ...DEFAULT_ATTRIBUTES.yearBuilt, show: true },
      floors: { ...DEFAULT_ATTRIBUTES.floors, show: true },
      furnished: { ...DEFAULT_ATTRIBUTES.furnished, show: false },
      petFriendly: { ...DEFAULT_ATTRIBUTES.petFriendly, show: false },
    },
  },
};

// Property type specific overrides
const PROPERTY_TYPE_OVERRIDES: Partial<Record<string, Partial<PropertyTypeConfig>>> = {
  APARTMENT: {
    icon: 'ri-building-line',
    attributes: {
      floorLevel: { ...DEFAULT_ATTRIBUTES.floorLevel, show: true },
      unitNumber: { ...DEFAULT_ATTRIBUTES.unitNumber, show: true },
    },
  },
  Apartment: {
    icon: 'ri-building-line',
    attributes: {
      floorLevel: { ...DEFAULT_ATTRIBUTES.floorLevel, show: true },
      unitNumber: { ...DEFAULT_ATTRIBUTES.unitNumber, show: true },
    },
  },
  STUDIO: {
    icon: 'ri-home-2-line',
    // Single-unit let: the studio IS the room, so no bedroom count.
    primaryAttributes: ['bathrooms', 'area', 'parking'],
    attributes: {
      bedrooms: { ...DEFAULT_ATTRIBUTES.bedrooms, show: false },
      floorLevel: { ...DEFAULT_ATTRIBUTES.floorLevel, show: true },
      unitNumber: { ...DEFAULT_ATTRIBUTES.unitNumber, show: true },
    },
  },
  Studio: {
    icon: 'ri-home-2-line',
    primaryAttributes: ['bathrooms', 'area', 'parking'],
    attributes: {
      bedrooms: { ...DEFAULT_ATTRIBUTES.bedrooms, show: false },
      floorLevel: { ...DEFAULT_ATTRIBUTES.floorLevel, show: true },
      unitNumber: { ...DEFAULT_ATTRIBUTES.unitNumber, show: true },
    },
  },
  ROOM: {
    // Single-unit let: renting one room — bedroom count is meaningless.
    primaryAttributes: ['bathrooms', 'area', 'parking'],
    attributes: {
      bedrooms: { ...DEFAULT_ATTRIBUTES.bedrooms, show: false },
      floorLevel: { ...DEFAULT_ATTRIBUTES.floorLevel, show: true },
      unitNumber: { ...DEFAULT_ATTRIBUTES.unitNumber, show: true },
    },
  },
  Room: {
    primaryAttributes: ['bathrooms', 'area', 'parking'],
    attributes: {
      bedrooms: { ...DEFAULT_ATTRIBUTES.bedrooms, show: false },
      floorLevel: { ...DEFAULT_ATTRIBUTES.floorLevel, show: true },
      unitNumber: { ...DEFAULT_ATTRIBUTES.unitNumber, show: true },
    },
  },
  PENTHOUSE: {
    icon: 'ri-building-2-line',
    attributes: {
      floorLevel: { ...DEFAULT_ATTRIBUTES.floorLevel, show: true },
      unitNumber: { ...DEFAULT_ATTRIBUTES.unitNumber, show: true },
    },
  },
  Penthouse: {
    icon: 'ri-building-2-line',
    attributes: {
      floorLevel: { ...DEFAULT_ATTRIBUTES.floorLevel, show: true },
      unitNumber: { ...DEFAULT_ATTRIBUTES.unitNumber, show: true },
    },
  },
  CONDO: {
    icon: 'ri-community-line',
    attributes: {
      floorLevel: { ...DEFAULT_ATTRIBUTES.floorLevel, show: true },
      unitNumber: { ...DEFAULT_ATTRIBUTES.unitNumber, show: true },
    },
  },
  Condo: {
    icon: 'ri-community-line',
    attributes: {
      floorLevel: { ...DEFAULT_ATTRIBUTES.floorLevel, show: true },
      unitNumber: { ...DEFAULT_ATTRIBUTES.unitNumber, show: true },
    },
  },
  VILLA: {
    icon: 'ri-home-4-line',
    primaryAttributes: ['bedrooms', 'bathrooms', 'area', 'parking', 'floors'],
    attributes: {
      floors: { ...DEFAULT_ATTRIBUTES.floors, show: true },
    },
  },
  FARM: {
    icon: 'ri-plant-line',
    attributes: {
      area: {
        show: true,
        labelKey: 'Farm Area',
        labelKeyAr: 'مساحة المزرعة',
        icon: 'ri-fullscreen-line',
      },
    },
  },
  HOTEL: {
    icon: 'ri-hotel-line',
    attributes: {
      bedrooms: {
        show: true,
        labelKey: 'Hotel Rooms',
        labelKeyAr: 'غرف الفندق',
        icon: 'ri-hotel-bed-line',
      },
    },
  },
  WAREHOUSE: {
    icon: 'ri-archive-line',
    primaryAttributes: ['area', 'parking'],
    attributes: {
      bedrooms: { ...DEFAULT_ATTRIBUTES.bedrooms, show: false },
      bathrooms: { ...DEFAULT_ATTRIBUTES.bathrooms, show: false },
    },
  },
  FACTORY: {
    icon: 'ri-building-3-fill',
    primaryAttributes: ['area', 'parking'],
    attributes: {
      bedrooms: { ...DEFAULT_ATTRIBUTES.bedrooms, show: false },
      bathrooms: { ...DEFAULT_ATTRIBUTES.bathrooms, show: false },
    },
  },
};

// Default icons by category
const CATEGORY_ICONS: Record<PropertyCategory, string> = {
  residential: 'ri-home-line',
  land: 'ri-landscape-line',
  commercial: 'ri-building-3-line',
  hospitality: 'ri-hotel-line',
  institutional: 'ri-government-line',
};

/**
 * Get the full configuration for a property type
 */
export function getPropertyTypeConfig(propertyType: string): PropertyTypeConfig {
  const category = PROPERTY_TYPE_CATEGORIES[propertyType] || 'residential';
  const categoryConfig = CATEGORY_CONFIGS[category];
  const typeOverrides = PROPERTY_TYPE_OVERRIDES[propertyType] || {};

  // Merge category config with type-specific overrides
  const mergedAttributes: Partial<Record<AttributeKey, AttributeConfig>> = {
    ...categoryConfig.attributes,
  };

  // Apply type-specific attribute overrides
  if (typeOverrides.attributes) {
    for (const [key, value] of Object.entries(typeOverrides.attributes)) {
      if (value) {
        mergedAttributes[key as AttributeKey] = {
          ...mergedAttributes[key as AttributeKey],
          ...value,
        };
      }
    }
  }

  return {
    category,
    icon: typeOverrides.icon || CATEGORY_ICONS[category],
    primaryAttributes: typeOverrides.primaryAttributes || categoryConfig.primaryAttributes || [],
    attributes: mergedAttributes,
    hideZeroValues: categoryConfig.hideZeroValues ?? true,
  };
}

/**
 * Get the category for a property type
 */
export function getPropertyCategory(propertyType: string): PropertyCategory {
  return PROPERTY_TYPE_CATEGORIES[propertyType] || 'residential';
}

/**
 * Check if property is a land type (no rooms expected)
 */
export function isLandProperty(propertyType: string): boolean {
  return getPropertyCategory(propertyType) === 'land';
}

/**
 * Check if property should show floor/unit info (apartments, etc.)
 */
export function shouldShowFloorInfo(propertyType: string): boolean {
  const config = getPropertyTypeConfig(propertyType);
  return config.attributes.floorLevel?.show || false;
}

/**
 * Get all supported property types
 */
export function getAllPropertyTypes(): string[] {
  return Object.keys(PROPERTY_TYPE_CATEGORIES);
}

// --- Per-type option gating (amenities + field groups) ---

const BUILDING_TYPE_IDS = ['building', 'commercial_building'];
export function isBuildingType(propertyType: string): boolean {
  return BUILDING_TYPE_IDS.includes(propertyType.toLowerCase());
}

// Amenity allow-lists by behaviour bucket. Ids match propertyFormOptions.AMENITIES_LIST.
// NOTE: 'parking' is intentionally absent — parking is captured by the dedicated
// "Parking Spaces" field, not an amenity chip (avoids a redundant double entry).
const RESIDENTIAL_AMENITIES = [
  'elevator','pool','garden','balcony','roof','driver_room','maid_room','storage',
  'security','intercom','central_ac','gym','playground','smart_home','basement','concierge',
  'pet_friendly','guest_house','courtyard',
];
// Buildings: building-level only — NO per-unit amenities (balcony/garden/roof/maid/driver/courtyard/guest_house/pool/pet_friendly).
const BUILDING_AMENITIES = [
  'elevator','security','intercom','central_ac','storage','basement','concierge','gym','playground','smart_home',
];
const COMMERCIAL_AMENITIES = [
  'elevator','security','intercom','central_ac','storage','basement','concierge','smart_home',
];
const HOSPITALITY_AMENITIES = [
  'elevator','pool','garden','security','central_ac','gym','playground','concierge','smart_home','courtyard','storage',
];

/**
 * Amenities valid for a property type. `listingType` is optional: when it's
 * 'sale', the rental-only `pet_friendly` policy is dropped (you don't advertise
 * a pet policy on a property you're selling — the buyer decides). This mirrors
 * the property-detail page, which already hides pet-friendly for sale listings.
 */
export function getAllowedAmenities(propertyType: string, listingType?: string): string[] {
  const id = propertyType.toLowerCase();
  let list: string[];
  if (isBuildingType(id)) {
    list = [...BUILDING_AMENITIES];
  } else {
    const category = getPropertyCategory(id.toUpperCase());
    switch (category) {
      case 'land': list = []; break;
      case 'commercial': case 'institutional': list = [...COMMERCIAL_AMENITIES]; break;
      case 'hospitality': list = [...HOSPITALITY_AMENITIES]; break;
      case 'residential': default: list = [...RESIDENTIAL_AMENITIES];
    }
  }
  if (listingType && listingType.toLowerCase() === 'sale') {
    list = list.filter((a) => a !== 'pet_friendly');
  }
  return list;
}

export interface VisibleFieldGroups {
  price: boolean;
  /** @deprecated kept for back-compat; equals `bedrooms`. Prefer the granular flags. */
  rooms: boolean;
  bedrooms: boolean;
  bathrooms: boolean;
  livingRooms: boolean;
  kitchens: boolean;
  /** Residential house attachment (detached / semi-detached / terraced). */
  attachment: boolean;
  floorInfo: boolean;
  furnishing: boolean;
  amenities: boolean;
  totalUnits: boolean;
  orientation: boolean;
  parking: boolean;
}

const FLOOR_FIELD_TYPES = ['apartment','floor','office','penthouse','duplex','shop','room','showroom','studio'];

// Residential dwellings (incl. single-room lets like room/studio — see SINGLE_UNIT_LETS).
const RESIDENTIAL_DWELLINGS = ['apartment','villa','house','townhouse','studio','penthouse','duplex','floor','room','condo'];
// Single-space lets: the whole unit IS the room, so asking "how many bedrooms?"
// is meaningless — they keep bathrooms + furnishing but never a bedroom count.
const SINGLE_UNIT_LETS = ['room','studio'];
// Multi-room homes that also have living rooms + kitchens. Excludes room/studio
// (single-space lets) — they get bedrooms/bathrooms but not living/kitchen.
const LIVING_SPACE_TYPES = ['apartment','villa','house','townhouse','penthouse','duplex','floor','condo'];
// Vacation dwellings — full living spaces.
const VACATION_DWELLINGS = ['rest_house','chalet'];
// Standalone houses where a detached / semi / terraced choice makes sense.
// Deliberately excludes flats/units (apartment, studio, penthouse, floor).
const HOUSE_TYPES = ['villa','house','townhouse','duplex'];

/**
 * Per-type field visibility. The previous version bundled bed/bath/living/kitchen
 * into one `rooms` flag that was true for ALL non-land, non-building types — so a
 * Factory or Warehouse wrongly showed Bedrooms / Living Rooms / Kitchens. This
 * computes each field group from the property type so commercial/industrial types
 * only get the fields that apply to them.
 */
export function getVisibleFieldGroups(propertyType: string): VisibleFieldGroups {
  const id = propertyType.toLowerCase();
  const upper = id.toUpperCase();
  const category = getPropertyCategory(upper);
  const building = isBuildingType(id);
  const land = category === 'land';

  const isResidentialDwelling = RESIDENTIAL_DWELLINGS.includes(id) && !building;
  const isSingleUnitLet = SINGLE_UNIT_LETS.includes(id) && !building;
  const isVacationDwelling = VACATION_DWELLINGS.includes(id);
  const isHotel = id === 'hotel';

  // Bedrooms: multi-room dwellings + vacation homes + hotels (rooms count).
  // Single-unit lets (room/studio) are excluded — the unit IS the room.
  const hasBedrooms = (isResidentialDwelling && !isSingleUnitLet) || isVacationDwelling || isHotel;
  // Living rooms + kitchens: multi-room homes only — not hotels, not single-room
  // lets (room/studio), never commercial.
  const hasLivingSpaces = (LIVING_SPACE_TYPES.includes(id) && !building) || isVacationDwelling;
  // Bathrooms: anything with bedrooms, single-unit lets (a room still has a
  // bathroom), plus commercial/institutional buildings (offices, shops, malls,
  // clinics… have WCs) — but not bare land or buildings.
  const hasBathrooms =
    hasBedrooms ||
    isSingleUnitLet ||
    (!land && !building && (category === 'commercial' || category === 'institutional'));

  return {
    price: !building,                       // buildings priced at building level elsewhere
    rooms: hasBedrooms,                      // back-compat alias
    bedrooms: hasBedrooms,
    bathrooms: hasBathrooms,
    livingRooms: hasLivingSpaces,
    kitchens: hasLivingSpaces,
    attachment: HOUSE_TYPES.includes(id) && !building,
    floorInfo: FLOOR_FIELD_TYPES.includes(id) && !building,
    // Furnishing applies to single-unit lets too (a furnished studio is common).
    furnishing: (hasBedrooms || isSingleUnitLet) && getPropertyTypeConfig(upper).attributes.furnished?.show !== false,
    amenities: !land,
    totalUnits: building,
    orientation: land,
    parking: !land,
  };
}
